import React, { useState } from 'react';
import { X, Send, MessageSquare, Star, AlertTriangle, CheckCircle2 } from 'lucide-react';
import DOMPurify from 'dompurify';
import { supabase } from '../../services/supabaseClient';
import { useTheme } from '../../context/ThemeContext';

// ── V-02 FIX: Sanitização na entrada ──
// Remove qualquer HTML/script do input ANTES de enviar ao banco.
const sanitize = (text) => {
  if (!text) return '';
  return DOMPurify.sanitize(text, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }).trim();
};

const FeedbackModal = ({ isOpen, onClose }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  const [formData, setFormData] = useState({
    name: '',
    type: 'feedback',
    message: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      // Sanitiza os dados antes de enviar ao banco
      const sanitizedData = {
        ...formData,
        name: sanitize(formData.name),
        message: sanitize(formData.message),
      };

      const { error: dbError } = await supabase
        .from('feedbacks')
        .insert([sanitizedData]);
      
      if (dbError) throw dbError;
      
      setSubmitted(true);
      setTimeout(() => {
        onClose();
        setSubmitted(false);
        setFormData({ name: '', type: 'feedback', message: '' });
      }, 3000);
      
    } catch (err) {
      // Erro silenciado em produção
      setError(`Erro ao enviar feedback: ${err.message || 'Erro desconhecido'}`);
    } finally {
      setLoading(false);
    }
  };

  const types = [
    { id: 'feedback', label: 'Sugestão', icon: <Star className="w-4 h-4" /> },
    { id: 'experience', label: 'Experiência', icon: <MessageSquare className="w-4 h-4" /> },
    { id: 'criticism', label: 'Crítica', icon: <AlertTriangle className="w-4 h-4" /> }
  ];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div className={`relative border rounded-2xl max-w-md w-full p-6 shadow-2xl animate-in zoom-in-95 duration-300 ${
        isDark ? 'bg-slate-800 border-cyan-500/30' : 'bg-white border-slate-200'
      }`}>
        <button
          onClick={onClose}
          className={`absolute top-4 right-4 p-2 rounded-full transition-colors ${
            isDark ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'
          }`}
        >
          <X size={20} />
        </button>

        {submitted ? (
          <div className="text-center py-8 flex flex-col items-center">
            <div className="w-16 h-16 bg-green-500/20 text-green-500 rounded-full flex items-center justify-center mb-4 animate-bounce">
              <CheckCircle2 size={32} />
            </div>
            <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>Feedback Enviado!</h3>
            <p className={`${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              Obrigado por nos ajudar a melhorar o Mapa Cultural.
            </p>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <h2 className={`text-2xl font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-800'}`}>Feedback</h2>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                Compartilhe sua experiência ou sugestão conosco.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className={`block text-xs font-semibold uppercase tracking-wider mb-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  Como quer ser chamado? (Opcional)
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Seu nome"
                  className={`w-full px-4 py-3 rounded-xl border transition-all outline-none focus:ring-2 focus:ring-cyan-500/50 ${
                    isDark 
                      ? 'bg-slate-900 border-slate-700 text-white placeholder-slate-600' 
                      : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'
                  }`}
                />
              </div>

              <div>
                <label className={`block text-xs font-semibold uppercase tracking-wider mb-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  Tipo de Feedback
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {types.map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setFormData({ ...formData, type: type.id })}
                      className={`flex flex-col items-center justify-center p-3 rounded-xl border transition-all ${
                        formData.type === type.id
                          ? 'bg-cyan-500 border-cyan-500 text-white shadow-lg shadow-cyan-500/20'
                          : isDark
                            ? 'bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-500'
                            : 'bg-slate-50 border-slate-200 text-slate-500 hover:border-slate-300'
                      }`}
                    >
                      {type.icon}
                      <span className="text-[10px] mt-1 font-medium">{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className={`block text-xs font-semibold uppercase tracking-wider mb-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  Sua Mensagem
                </label>
                <textarea
                  required
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Conte-nos o que você achou..."
                  rows="4"
                  className={`w-full px-4 py-3 rounded-xl border transition-all outline-none focus:ring-2 focus:ring-cyan-500/50 resize-none ${
                    isDark 
                      ? 'bg-slate-900 border-slate-700 text-white placeholder-slate-600' 
                      : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'
                  }`}
                />
              </div>

              {error && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500 text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold rounded-xl transition-all shadow-lg shadow-cyan-500/30 transform active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Send size={18} />
                    Enviar Feedback
                  </>
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default FeedbackModal;
